import React from "react";
import type { JSX, PropsWithChildren, HTMLAttributes, Ref } from "react";

import cn from "classnames";

import { Box, boxForwardRef } from "../Box/Box";

import type { BoxComponentWithDefault } from "../Box/Box";

import classes from "./Gap.styl";

interface IGap
    extends PropsWithChildren<
        Omit<HTMLAttributes<HTMLDivElement>, "className">
    > {
    /**
     * Необходимо передать селектор, в котором будут указаны следующие переменные:
     * * `--row-gap` - обязательно;
     * * `--column-gap` - опционально. Если не передать, то будет использоваться `--row-gap`.
     *
     * А также: в этот селектор стоит выносить те стили, которые влияют на отображение контейнера
     * (всё, что может "поломать" отрицательный margin)
     * @example .selector {
     *     --row-gap: $base-x;
     *     --column-gap: $base-x;
     * }
     */
    className: string;
    /**
     * В этот селектор стоит выносить те стили, которые влияют на позиционирование детей внутри контейнера
     * (например, align-items)
     */
    contentClassName?: string;
    /**
     * ref для обёртки Gap
     */
    wrapperRef?: Ref<HTMLDivElement>;
}

/**
 * Компонент для реализации gap-ов между детьми. Работает на основе ds-margin-gap и css-переменных.
 * @see CoreInputText
 */
export const Gap = boxForwardRef<BoxComponentWithDefault<"div", IGap>>(
    (
        {
            as = "div",
            contentClassName,
            className,
            wrapperRef,
            children,
            ...props
        },
        ref,
    ): JSX.Element => {
        return (
            <div
                className={cn(classes.wrapper, className)}
                data-tsid="gap"
                ref={wrapperRef}
                {...props}
            >
                <Box
                    as={as}
                    className={cn(classes.gap, contentClassName)}
                    ref={ref}
                >
                    {children}
                </Box>
            </div>
        );
    },
);
